import { SortEnumType } from '@gql/graphql';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import type { SelectedFilters } from 'uikit';

import type { IGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getCatalogsNamesByFilter } from '../../CatalogTable/catalogQueries';
import { catalogRatiosGql } from '../catalogRatiosFilterQueries';
import { getValuesFromSaved, getWhereVariables } from '../catalogRatiosHelpers';

const requestId = 'catalogRatios';

export const useCatalogRatiosData = ({ catalogId }: { catalogId: string | undefined }) => {
    const [queryWhere, setQueryWhere] = useState<SelectedFilters>({});

    const dispatch = useDispatch();

    const memoizedRequestIds = useMemo(() => [requestId, `getCatalogsNamesByFilter_${catalogId}`], [catalogId]);

    const [catalogRatiosReset] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(() => {
        catalogRatiosReset();
    }, [catalogRatiosReset]);

    const catalogsVariables = useMemo(
        () => ({
            take: 1,
            where: { uniqueId: { in: [String(catalogId)] } },
        }),
        [catalogId],
    );

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: getValuesFromSaved({ setQueryWhere }),
                withFakeSetter: true,
            }),
            [],
        ),
    );

    const ratiosVariables = useMemo(
        () => ({
            where: getWhereVariables(queryWhere),
            order: [{ code: SortEnumType.Asc }],
        }),
        [queryWhere],
    );

    const [getCatalogsNamesByFilterData, getCatalogsNamesByFilterStatus] = useRequest({
        requestId: `getCatalogsNamesByFilter_${catalogId}`,
        query: getCatalogsNamesByFilter,
        variables: catalogsVariables,
    });

    const [catalogRatiosData, catalogRatiosStatus, catalogRatiosError, catalogRatiosRequest] = useRequest({
        requestId,
        query: catalogRatiosGql,
        variables: ratiosVariables,
        withEffect: false,
    });

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;

        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort) {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return [{ code: SortEnumType.Asc }];
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: catalogRatiosRequest,
        requestVariables: ratiosVariables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (catalogRatiosData?.catalogRatios?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                catalogRatiosData.catalogRatios.items ?? [],
                catalogRatiosData.catalogRatios?.totalCount,
            );
        }
    }, [getRowParams, catalogRatiosData?.catalogRatios?.items, catalogRatiosData?.catalogRatios?.totalCount]);

    const updateCatalogRatios = useCallback(() => {
        dispatch(catalogRatiosRequest(ratiosVariables));
    }, [catalogRatiosRequest, dispatch, ratiosVariables]);

    const totalCount = catalogRatiosData?.catalogRatios?.totalCount ?? 0;

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalCount, getRowParams?.current?.endRow]);

    const isError = useMemo(() => catalogRatiosStatus === RequestStatus.ERROR, [catalogRatiosStatus]);

    return useMemo(
        () => ({
            catalogName: getCatalogsNamesByFilterData?.catalogs?.items?.[0]?.name,
            getCatalogsNamesByFilterStatus,
            catalogRatiosStatus,
            catalogRatiosError,
            totalCount,
            fetchedItems,
            updateCatalogRatios,
            isError,
            dataSource,
        }),
        [
            getCatalogsNamesByFilterData?.catalogs?.items,
            getCatalogsNamesByFilterStatus,
            catalogRatiosStatus,
            catalogRatiosError,
            totalCount,
            fetchedItems,
            updateCatalogRatios,
            isError,
            dataSource,
        ],
    );
};
